import React, { Fragment } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CustomLineItem, LineItem } from '@commercetools/platform-sdk';
import { calculateTotalDiscount, priceToString } from '../../../utils';
import {
  getAttributeValue,
  findVariantAttributeByName,
} from '../../../utils/variants';
import { getCookie } from '../../../utils/common/common';
import {
  WARRANTY_CUSTOM_LINE_ITEM_TYPE,
  FEEL_ATTRIBUTE_NAME,
  FEEL_VARIANT_NAME_COOKIE,
} from '../../../utils/common/constants';

import messages from './messages';
import styles from './OrderSummary.module.scss';
import useOrderDataSource from '../../../hooks/useOrderDataSource/useOrderDataSource';

type OrderSummaryDataSourcesType = 'cart' | 'order';

interface OrderSummaryProps {
  dataSource: OrderSummaryDataSourcesType;
}
const OrderSummary = ({ dataSource }: OrderSummaryProps) => {
  const { locale, formatNumber, formatMessage } = useIntl();
  const orderSummaryDataSource = useOrderDataSource(dataSource);
  const lineItems = orderSummaryDataSource?.lineItems;

  const hideFeelCookie = getCookie(FEEL_VARIANT_NAME_COOKIE);
  const hideFeelVariantName =
    hideFeelCookie?.value === 'hide_feel_variant_test';
  

  interface WarrantyItem extends CustomLineItem {
    id: string;
    name: { [key: string]: string };
  }

  interface ProductName {
    [key: string]: string;
  }

  interface RenderWarrantyItemProps {
    warrantyItem: WarrantyItem;
    productName: ProductName;
  }

  const warrantyItems: WarrantyItem[] =
  orderSummaryDataSource?.customLineItems?.filter(
    (customLineItem): customLineItem is WarrantyItem =>
      customLineItem.custom !== undefined &&
      customLineItem.custom.fields?.type === WARRANTY_CUSTOM_LINE_ITEM_TYPE,
  ) ?? [];
  


  const renderWarrantyItem = ({
    warrantyItem,
    productName,
  }: RenderWarrantyItemProps): JSX.Element =>
    warrantyItem && (
      <div className={styles['order-summary-item']}>
        <div className={styles['item-info']}>
          <div>
            {warrantyItem.name[locale]}
          </div>
          <div>
               {priceToString({
                price: warrantyItem.totalPrice,
                formatNumber,
                formatMessage,
                maximumFractionDigits: 2,
              })}
          </div>
        </div>
        <div className={styles['item--detail-info']}>
          <div>
            {formatMessage(messages.warrantyProduct, {
              product: productName[locale],
            })}
          </div>
          <div>
            {formatMessage(messages.quantity, {
              quantity: warrantyItem.quantity,
            })}
          </div>
        </div>
      </div>
      
    );

  return (
    <div
      className={styles['order-summary-wrapper']}
    >
      {lineItems?.map(({ id, name, totalPrice, quantity, variant, custom }: LineItem) => {
        const associatedWarrantyItems = warrantyItems.filter((item: WarrantyItem): boolean =>
          (item?.custom && Array.isArray(item?.custom?.fields?.lineItemsIds)
          && item.custom.fields.lineItemsIds.includes(id)) || false,
        );
        const discountsJson: string | undefined =
          custom?.fields?.discounts_json as string | undefined;

        let discounted:boolean = false;
        if (discountsJson) {
            const parsed: unknown = JSON.parse(discountsJson);
            discounted = Array.isArray(parsed) && parsed.length > 0;

        }
        const sizeAttributeValue = getAttributeValue(variant.attributes, 'size') as { label: string }
          || undefined;
        return (
          <Fragment key={id}>
            <div className={styles['order-summary-item']}>
              <div className={styles['item-info']}>
                <div>
                  <span>{name[locale]} </span>
                  <div className={styles['item--detail-info']}>
                    <div>
                      {!!findVariantAttributeByName(variant, FEEL_ATTRIBUTE_NAME) &&
                        hideFeelVariantName
                        ? sizeAttributeValue.label
                        : getAttributeValue(variant.attributes, 'title')}
                    </div>
                    <div>
                      {formatMessage(messages.quantity, { quantity })}
                    </div>
                  </div>
                </div>
                <div className={styles['pricing-info']}>
                  {discounted && <div className={styles['strikthrough-price']}>
                    {priceToString({
                      price: {
                        ...totalPrice,
                        centAmount:
                          totalPrice.centAmount +
                          calculateTotalDiscount({ quantity, custom }),
                      },
                      formatNumber,
                      formatMessage,
                      maximumFractionDigits: 2,
                    })}
                  </div>}
                  <div className={classnames({ [styles['promo-price']]: discounted })}>
                    {priceToString({
                      price: totalPrice,
                      formatNumber,
                      formatMessage,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
              </div>
            </div>
            {associatedWarrantyItems.map((warrantyItem) =>
              renderWarrantyItem({
                warrantyItem,
                productName: name,
              }),
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

export default OrderSummary;
